import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import {
  ArrowRight,
  ArrowUpRight,
  Ticket,
  Trophy,
  Sparkles,
  Package,
  Send,
  MapPin,
  Users,
  ShieldCheck,
  Truck,
  Clock,
  ChevronDown,
  Calendar,
  ShoppingBag,
} from "lucide-react";
import SoccerBall from "@/components/SoccerBall";
import HeroMosaic from "@/components/HeroMosaic";
import BlackOneOfOneFeatureCard from "@/components/home/BlackOneOfOneFeatureCard";
import InstagramCommunity from "@/components/InstagramCommunity";
import { FutureNetworkHome } from "@/components/FutureCollectorNetwork";
import SEO from "@/components/SEO";
import { SkelCard } from "@/components/Skeleton";
import api from "@/lib/api";
import { getEnabledFeaturedProducts } from "@/config/featuredProducts";
import { useSiteSettings } from "@/hooks/useSiteSettings";

/* ------------------------------------------------------------------ page */

export default function Home() {
  return (
    <div data-testid="home-page">
      <SEO
        title="Panini Stickers, Album Certificates & Collector Profiles"
        description="CollectorUp, by Gil Maakaroun. Shop albums, packs, boxes, and Gold Crumples, order missing stickers, RSVP to local trading events, and get completed albums independently inspected and issued a certificate."
        path="/"
      />
      <Hero />
      <FutureNetworkHome />
      <FeaturedProducts />
      <UpcomingEvents />
      <AlbumCertification />
      <InstagramCommunity />
      <WhyCollectWithGil />
      <FAQ />
      <CollectorIdentityCTA />
    </div>
  );
}

/* ---------------------------------------------------------- 1. HERO */

function Hero() {
  const settings = useSiteSettings();
  const headline = (settings.hero_headline || "The home for Panini collectors.").trim();
  // Try to split a 3-clause headline into three visual lines with color emphasis.
  // Falls back gracefully to a single-line render for unusual custom copy.
  const words = headline.replace(/[.!?]+$/, "").split(/\s+/);
  const useSplit = words.length >= 3 && words.length <= 7;
  let l1 = "", l2 = "", l3 = "";
  if (useSplit) {
    l3 = words[words.length - 1];
    if (words.length === 3) {
      l1 = words[0];
      l2 = words[1];
    } else if (words.length === 4) {
      l1 = words[0];
      l2 = words.slice(1, 3).join(" ");
    } else {
      l1 = words.slice(0, 2).join(" ");
      l2 = words.slice(2, words.length - 1).join(" ");
    }
  }

  return (
    <section className="hero-black" data-testid="home-hero">
      <div className="container-gm relative pt-14 pb-16 md:pt-20 md:pb-24">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 items-center">
          <div className="lg:col-span-7">
            <motion.div
              initial={{ opacity: 0, y: 18 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }}
            >
              <div className="flex items-center gap-3 flex-wrap">
                <span className="sticker sticker-red">MATCHDAY</span>
                <span className="sticker sticker-white">EST. USA</span>
              </div>
              {useSplit ? (
                <h1
                  className="h-display text-white mt-6 text-5xl sm:text-6xl md:text-7xl lg:text-[5.5rem] uppercase leading-[0.95]"
                  data-testid="home-hero-title"
                >
                  {l1}<br />
                  {l2 && (
                    <>
                      {l2.replace(/PANINI/i, "").trim()}{" "}
                      <span className="text-[var(--panini-yellow)]">
                        {/Panini/i.test(l2) ? "PANINI" : ""}
                      </span>
                      <br />
                    </>
                  )}
                  <span className="text-[var(--panini-red)]">{l3}.</span>
                </h1>
              ) : (
                <h1
                  className="h-display text-white mt-6 text-4xl sm:text-5xl md:text-6xl uppercase leading-[1.02]"
                  data-testid="home-hero-title"
                >
                  {headline}
                </h1>
              )}
              <p
                className="text-white/85 mt-6 max-w-xl text-base md:text-lg leading-relaxed"
                data-testid="home-hero-subheadline"
              >
                {settings.hero_subheadline}
              </p>

              {/* four primary actions — visible above the fold */}
              <div
                className="grid grid-cols-2 md:grid-cols-4 gap-3 mt-8 max-w-2xl"
                data-testid="home-primary-actions"
              >
                <PrimaryAction
                  to="/shop"
                  icon={<ShoppingBag size={18} />}
                  label="Store"
                  testid="hero-action-store"
                  variant="yellow"
                />
                <PrimaryAction
                  to="/order-missing"
                  icon={<Send size={18} />}
                  label="Missing Stickers"
                  testid="hero-action-missing"
                  variant="red"
                />
                <PrimaryAction
                  to="/events"
                  icon={<Ticket size={18} />}
                  label="Events"
                  testid="hero-action-events"
                  variant="green"
                />
                <PrimaryAction
                  to="/certificates"
                  icon={<Trophy size={18} />}
                  label="Certificates"
                  testid="hero-action-registry"
                  variant="black"
                />
              </div>

              {/* Black 1/1 Tracker feature card (Phase 1 · full-width premium destination). */}
              <div className="mt-4 max-w-2xl">
                <BlackOneOfOneFeatureCard />
              </div>

              {/* Trust strip */}
              <div className="flex flex-wrap items-center gap-x-6 gap-y-3 mt-8 text-white/80 text-sm">
                <TrustPill icon={<Truck size={14} />} label="Nationwide shipping" />
                <TrustPill icon={<ShieldCheck size={14} />} label="Independent certification" />
                <TrustPill icon={<Clock size={14} />} label="Same-day reply" />
                <TrustPill icon={<Users size={14} />} label="Family-friendly" />
              </div>
            </motion.div>
          </div>

          <div className="lg:col-span-5 relative">
            <motion.div
              initial={{ opacity: 0, scale: 0.94, rotate: -3 }}
              animate={{ opacity: 1, scale: 1, rotate: -3 }}
              transition={{ duration: 0.6, delay: 0.15 }}
              className="rounded-3xl overflow-hidden border-4 border-white shadow-[10px_10px_0_var(--panini-yellow)] max-w-[380px] mx-auto lg:mx-0"
            >
              {settings.hero_image_url ? (
                <img
                  src={settings.hero_image_url}
                  alt={settings.business_name || "Gil Maakaroun"}
                  className="w-full h-full object-cover object-center aspect-[5/7]"
                  data-testid="home-hero-photo"
                />
              ) : (
                <HeroMosaic />
              )}
            </motion.div>
          </div>
        </div>
      </div>
      <div className="field-line" aria-hidden />
    </section>
  );
}

function PrimaryAction({ to, icon, label, testid, variant }) {
  const map = {
    yellow: "bg-[var(--panini-yellow)] text-[var(--ink)]",
    red: "bg-[var(--panini-red)] text-white",
    green: "bg-[var(--field-green)] text-white",
    black: "bg-white text-[var(--ink)]",
  };
  return (
    <Link
      to={to}
      data-testid={testid}
      className={`group flex flex-col items-start gap-3 p-4 rounded-2xl border-2 border-[var(--ink)] shadow-[4px_4px_0_var(--ink)] hover:-translate-y-0.5 transition-transform ${map[variant]}`}
    >
      <div className="flex items-center justify-between w-full">
        <span className="w-10 h-10 rounded-xl border-2 border-[var(--ink)] bg-white/20 flex items-center justify-center">
          {icon}
        </span>
        <ArrowUpRight
          size={18}
          className="opacity-70 group-hover:opacity-100 group-hover:translate-x-0.5 transition-all"
        />
      </div>
      <div className="font-display text-base leading-tight">{label}</div>
    </Link>
  );
}

function TrustPill({ icon, label }) {
  return (
    <div className="flex items-center gap-2">
      <span className="w-7 h-7 rounded-full border border-white/30 bg-white/10 flex items-center justify-center text-[var(--panini-yellow)]">
        {icon}
      </span>
      <span className="font-semibold">{label}</span>
    </div>
  );
}

/* ---------------------------------------------- 3. WHY COLLECT WITH GIL */

const whyPoints = [
  { Icon: Users, title: "Trusted collector community", body: "A real hobbyist-run space where kids, parents, and lifelong collectors are welcome.", color: "green" },
  { Icon: Calendar, title: "Weekly Sticker Trading Events", body: "Regular local trading events where you can meet, trade, and pick up orders in person.", color: "red" },
  { Icon: Truck, title: "Nationwide shipping", body: "All 50 states and D.C. Fast, tracked, and packaged with the same care we'd expect ourselves.", color: "yellow" },
  { Icon: Send, title: "Personalized missing-sticker service", body: "Send us your missing sticker list. Once payment is received, your order is confirmed and we immediately begin preparing it.", color: "red" },
  { Icon: Trophy, title: "Independent album certification", body: "Every completed album we accept is physically inspected and given a numbered public record.", color: "black" },
  { Icon: Clock, title: "Fast response times", body: "Same-day replies on most requests.", color: "green" },
];

function WhyCollectWithGil() {
  return (
    <section className="py-16 md:py-24 bg-[var(--panini-yellow-soft)] border-y-2 border-[var(--ink)]" data-testid="home-why">
      <div className="container-gm">
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4 mb-10">
          <div>
            <div className="kicker kicker-red">Why Collect With Gil</div>
            <h2 className="h-display text-4xl md:text-6xl mt-3">
              A hobby home,<br />run by a collector.
            </h2>
          </div>
          <p className="text-[var(--smoke)] max-w-md">
            The kind of service, community, and honesty we&apos;d want if we were
            the ones ordering the last few stickers to finish an album.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5 md:gap-6">
          {whyPoints.map((p) => {
            const Icon = p.Icon;
            const iconClass = {
              yellow: "bg-[var(--panini-yellow)] text-[var(--ink)]",
              red: "bg-[var(--panini-red)] text-white",
              green: "bg-[var(--field-green)] text-white",
              black: "bg-[var(--ink)] text-[var(--panini-yellow)]",
            }[p.color];
            return (
              <div
                key={p.title}
                className="rounded-2xl border-2 border-[var(--ink)] bg-white p-6 flex gap-4"
              >
                <div className={`shrink-0 w-11 h-11 rounded-xl border-2 border-[var(--ink)] flex items-center justify-center ${iconClass}`}>
                  <Icon size={20} />
                </div>
                <div>
                  <h3 className="font-display text-lg leading-tight">{p.title}</h3>
                  <p className="text-[var(--smoke)] mt-1.5 text-sm leading-relaxed">{p.body}</p>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
}

/* ---------------------------------------------- 4. FEATURED PRODUCTS */

function FeaturedProducts() {
  const products = getEnabledFeaturedProducts();

  // Hide the entire section until real Shopify products are configured.
  // No decorative placeholders, no public setup instructions.
  if (products.length === 0) return null;

  return (
    <section className="container-gm py-16 md:py-24" data-testid="home-featured-products">
      <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4 mb-10">
        <div>
          <div className="kicker kicker-red">Featured Products</div>
          <h2 className="h-display text-4xl md:text-6xl mt-3">Latest in the store.</h2>
          <p className="text-[var(--smoke)] max-w-md mt-3 text-sm">
            Curated products from previous World Cups through the upcoming
            USA / Canada / Mexico 2026 edition — all shipped nationwide.
          </p>
        </div>
        <Link to="/shop" className="btn-black" data-testid="featured-products-cta">
          Enter the Store <ArrowUpRight size={16} />
        </Link>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
        {products.map((p, i) => (
          <a
            key={p.productUrl}
            href={p.productUrl}
            target="_blank"
            rel="noopener noreferrer"
            data-testid={`featured-product-${i}`}
            className="card-fun group block p-0 overflow-hidden"
          >
            <div className="relative aspect-square bg-[var(--panini-yellow-soft)] overflow-hidden">
              <img
                src={p.imageUrl}
                alt={p.title}
                loading="lazy"
                className="w-full h-full object-cover object-center group-hover:scale-[1.02] transition-transform"
                data-testid={`featured-product-image-${i}`}
              />
              {p.badge && (
                <span
                  className="absolute top-3 left-3 rounded-full bg-[var(--ink)] text-white px-2.5 py-0.5 text-[10px] font-bold uppercase tracking-widest"
                  data-testid={`featured-product-badge-${i}`}
                >
                  {p.badge}
                </span>
              )}
            </div>
            <div className="p-5 border-t-2 border-[var(--ink)]">
              <div className="kicker kicker-red">Shop Now</div>
              <div className="flex items-baseline justify-between gap-2 mt-1">
                <h3 className="font-display text-lg leading-tight">{p.title}</h3>
                {p.displayPrice && (
                  <span
                    className="font-mono text-[var(--panini-red)] text-sm font-bold whitespace-nowrap"
                    data-testid={`featured-product-price-${i}`}
                  >
                    {p.displayPrice}
                  </span>
                )}
              </div>
              {p.tagline && (
                <p className="text-[var(--smoke)] mt-1.5 text-xs leading-relaxed">{p.tagline}</p>
              )}
            </div>
          </a>
        ))}
      </div>
    </section>
  );
}

/* ---------------------------------------------- 5. UPCOMING EVENTS */

function useEvents() {
  const [events, setEvents] = useState(null);
  useEffect(() => {
    api
      .get("/events")
      .then((r) => setEvents(Array.isArray(r.data) ? r.data : []))
      .catch(() => setEvents([]));
  }, []);
  return events;
}

function formatWhen(iso) {
  if (!iso) return "";
  try {
    return new Date(iso).toLocaleString(undefined, {
      weekday: "short",
      month: "short",
      day: "numeric",
      hour: "numeric",
      minute: "2-digit",
    });
  } catch {
    return iso;
  }
}

function UpcomingEvents() {
  const events = useEvents();
  const upcoming = (events || []).slice(0, 3);

  return (
    <section className="py-16 md:py-24 bg-[var(--ink)] text-white" data-testid="home-events">
      <div className="container-gm">
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4 mb-10">
          <div>
            <div className="kicker kicker-yellow">Upcoming Events</div>
            <h2 className="h-display text-4xl md:text-6xl mt-3 text-white">
              Come trade in person.
            </h2>
            <p className="text-white/70 max-w-md mt-3 text-sm">
              Free RSVP. Family-friendly. Bring your doubles.
            </p>
          </div>
          <Link to="/events" className="btn-yellow" data-testid="home-events-see-all">
            See all events <ArrowRight size={16} />
          </Link>
        </div>

        {events === null && (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-5" data-testid="home-events-loading">
            <SkelCard variant="dark" />
            <SkelCard variant="dark" />
            <SkelCard variant="dark" />
          </div>
        )}
        {events && upcoming.length === 0 && (
          <div className="rounded-2xl border-2 border-white/20 bg-white/5 p-8 text-center">
            <SoccerBall size={38} strokeColor="#FFCB05" className="mx-auto" />
            <div className="kicker kicker-yellow mt-4">No events on the schedule yet</div>
            <p className="text-white/70 mt-2 max-w-md mx-auto">
              We&apos;re lining up the next round of trading events. Join the
              mailing list to be first to know.
            </p>
          </div>
        )}
        {upcoming.length > 0 && (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
            {upcoming.map((e) => (
              <Link
                key={e.id}
                to={`/events/${e.id}`}
                data-testid={`home-event-${e.id}`}
                className="rounded-2xl border-2 border-white/25 bg-white/5 p-6 hover:border-[var(--panini-yellow)] transition-colors block"
              >
                <div className="flex items-center gap-2 text-[var(--panini-yellow)] text-sm">
                  <Calendar size={15} /> {formatWhen(e.starts_at)}
                </div>
                <h3 className="font-display text-xl mt-3 leading-tight text-white">
                  {e.title}
                </h3>
                <div className="mt-3 flex items-center gap-2 text-white/70 text-sm">
                  <MapPin size={14} /> {e.location}
                </div>
                <div className="mt-4 text-[var(--panini-yellow)] text-sm font-bold">
                  RSVP now →
                </div>
              </Link>
            ))}
          </div>
        )}
      </div>
    </section>
  );
}

/* ---------------------------------------------- 6. ALBUM CERTIFICATION */

function AlbumCertification() {
  return (
    <section className="py-16 md:py-24" data-testid="home-certification">
      <div className="container-gm grid grid-cols-1 md:grid-cols-12 gap-10 items-center">
        <div className="md:col-span-6 order-2 md:order-1">
          <div className="kicker kicker-green">Album Certification</div>
          <h2 className="h-display text-4xl md:text-6xl mt-3">
            Complete an album?<br />
            <span className="text-[var(--field-green)]">Get it on the record.</span>
          </h2>
          <p className="mt-5 text-[var(--smoke)] leading-relaxed max-w-md">
            We personally inspect every completed album we accept and issue a
            numbered certificate — for example{" "}
            <span className="font-mono font-bold">GM-2026-0001</span> — logged in
            the public certificates with the level of privacy you choose.
          </p>
          <ul className="mt-6 space-y-3 text-sm">
            <Bullet color="green">
              <Sparkles size={16} /> Independently inspected and recorded by Gil Maakaroun.
            </Bullet>
            <Bullet color="yellow">
              <Users size={16} /> Full name, first name + initial, or hidden — your call.
            </Bullet>
            <Bullet color="red">
              <MapPin size={16} /> Permanent public URL for every record.
            </Bullet>
          </ul>
          <Link to="/certificates" className="btn-black mt-8" data-testid="cert-feature-cta">
            <Trophy size={16} /> View Certificates
          </Link>
        </div>

        <div className="md:col-span-6 order-1 md:order-2">
          <div className="relative">
            <div className="premium-wrap rounded-3xl p-10 md:p-14 border-4 border-[var(--ink)] shadow-[10px_10px_0_var(--panini-yellow)]">
              <div className="cert-frame p-8 md:p-10 text-center">
                <div className="overline-gold">Certificate of Completion</div>
                <div className="h-serif text-2xl md:text-4xl mt-4 text-white">
                  Numbered & Verified
                </div>
                <div className="font-mono text-[var(--gm-gold)] tracking-widest mt-4 text-lg">
                  GM-2026-0001
                </div>
                <p className="text-white/60 text-xs mt-5 leading-relaxed">
                  Independently inspected and recorded by Gil Maakaroun.
                </p>
              </div>
            </div>
            <div className="absolute -top-5 -left-5 sticker sticker-red rotate-[-8deg]">
              Numbered
            </div>
            <div className="absolute -bottom-5 -right-5 sticker sticker-green rotate-[6deg]">
              Verified by Gil
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

function Bullet({ color, children }) {
  const map = {
    green: "bg-[var(--field-green)] text-white",
    yellow: "bg-[var(--panini-yellow)] text-[var(--ink)]",
    red: "bg-[var(--panini-red)] text-white",
  };
  return (
    <li className="flex items-start gap-3 text-[var(--ink)]">
      <span className={`w-7 h-7 rounded-full border-2 border-[var(--ink)] flex items-center justify-center shrink-0 ${map[color]}`}>
        {children[0]}
      </span>
      <span className="pt-1">{children[1]}</span>
    </li>
  );
}

/* ---------------------------------------------- 8. INSTAGRAM COMMUNITY */
// Rendered from <InstagramCommunity /> imported at the top of the file.
// (Replaces the previous Community Gallery — no placeholder photo grid.)

/* ---------------------------------------------- 9. FAQ */

const faqs = [
  {
    q: "Do you ship nationwide?",
    a: "Yes. We ship to all 50 states and Washington, D.C. Standard shipping is calculated at checkout in the store and stated up-front on missing-sticker quotes.",
  },
  {
    q: "How does the missing-sticker service work?",
    a: "Customers submit their missing sticker list. We manually verify availability, and our pricing engine generates a complete pricing breakdown. If the customer agrees, payment is completed through the website. Once payment is received, the order is officially confirmed and we immediately begin preparing it.",
  },
  {
    q: "Are events free?",
    a: "Yes. All community trading events currently listed are free to RSVP. Capacity may be limited, so RSVP early.",
  },
  {
    q: "Is your certificate program official?",
    a: "No. Our program is independently operated and is not affiliated with, endorsed by, or approved by Panini or FIFA. Every certificate is independently inspected and recorded by Gil Maakaroun.",
  },
  {
    q: "Can my child use this site?",
    a: "The site is designed to be family-safe. For minors, we ask a parent or guardian to complete forms on their behalf and provide consent.",
  },
];

function FAQ() {
  return (
    <section className="container-gm py-16 md:py-24" data-testid="home-faq">
      <div className="max-w-2xl">
        <div className="kicker kicker-red">Frequently Asked Questions</div>
        <h2 className="h-display text-4xl md:text-6xl mt-3">Good questions.</h2>
      </div>
      <div className="mt-10 max-w-3xl divide-y-2 divide-[var(--ink)] border-2 border-[var(--ink)] rounded-3xl overflow-hidden bg-white">
        {faqs.map((f, i) => (
          <details key={i} className="group" data-testid={`home-faq-${i}`}>
            <summary className="cursor-pointer list-none flex items-center justify-between p-6 md:p-7 hover:bg-[var(--panini-yellow-soft)]">
              <span className="font-display text-lg md:text-xl pr-6">{f.q}</span>
              <ChevronDown
                size={22}
                className="shrink-0 transition-transform group-open:rotate-180"
              />
            </summary>
            <div className="px-6 md:px-7 pb-6 md:pb-7 text-[var(--smoke)] leading-relaxed text-sm">
              {f.a}
            </div>
          </details>
        ))}
      </div>
    </section>
  );
}

/* ---------------------------------------------- 10. FINAL COLLECTOR IDENTITY CTA */

function CollectorIdentityCTA() {
  return (
    <section
      className="py-20 md:py-28 bg-[var(--panini-yellow)] border-y-2 border-[var(--ink)]"
      data-testid="home-collector-identity-cta"
    >
      <div className="container-gm text-center max-w-3xl mx-auto">
        <span className="sticker sticker-red rotate-[-2deg]">JOIN EARLY</span>
        <h2 className="h-display text-4xl md:text-6xl mt-6 leading-tight">
          Create Your Collector Identity.
        </h2>
        <p className="text-[var(--ink)]/85 mt-5 leading-relaxed text-base md:text-lg max-w-xl mx-auto">
          Your account starts today with certificates and your permanent collector identity.
          As the Collector Network grows, your profile will automatically gain access
          to future features.
        </p>
        <div className="mt-8 flex flex-col md:flex-row gap-3 justify-center items-center">
          <Link
            to="/register"
            className="btn-red inline-flex items-center justify-center gap-2 min-h-[56px] px-8 py-4 text-base"
            data-testid="home-final-cta-register"
          >
            Create My Collector Account <ArrowRight size={18} />
          </Link>
          <Link
            to="/registry"
            className="inline-flex items-center justify-center gap-2 min-h-[56px] px-8 py-4 text-base rounded-full border-2 border-[var(--ink)] bg-white text-[var(--ink)] font-display hover:bg-[var(--ink)] hover:text-white transition-colors"
            data-testid="home-final-cta-registry"
          >
            View Certificates
          </Link>
        </div>
      </div>
    </section>
  );
}
